import type { OverviewGranularity, PrintOverviewResponseRow } from "./types";
import { API_GRANULARITY } from "./logic";

/**
 * Deterministic mock dataset shaped EXACTLY like the API response (the
 * dynamic `Dbo520jh.K005.<granularity>` time field, string measures —
 * types.ts). The generator always builds DAY-level values first, then sums
 * them into the requested API granularity — so the `week` level (fetched as
 * days, API_GRANULARITY) aggregates exactly like it will against the real
 * backend. Stable across reloads; swapping in CubeJS is a body-only change
 * in `state.ts`.
 */

/** Mock span: 2023-01-01 → 2026-09-30 (full years + a partial one). */
const MOCK_START = { y: 2023, m: 1, d: 1 };
const MOCK_END = { y: 2026, m: 9, d: 30 };

/** Per-day bases: printed length (ft), ink (L), print time (s). */
const DAILY_BASE = {
  jobCount: 220,
  printedLength: 430_000,
  inkUsage: 52,
  printTimeSeconds: 16_500,
};

/** Deterministic PRNG (LCG) so the fake data is stable across renders. */
function makeRand(seed = 20260928) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Local-date triple → API-style ISO UTC-midnight stamp. */
function isoOf(y: number, m: number, d: number): string {
  return `${y}-${pad(m)}-${pad(d)}T00:00:00.000Z`;
}

/** Per-day values with weekday dips, a seasonal wave and seeded jitter. */
function generateDailyValues(): {
  date: Date;
  jobCount: number;
  printedLength: number;
  inkUsage: number;
  printTimeSeconds: number;
}[] {
  const rand = makeRand();
  const values = [];
  const date = new Date(MOCK_START.y, MOCK_START.m - 1, MOCK_START.d);
  const end = new Date(MOCK_END.y, MOCK_END.m - 1, MOCK_END.d);
  while (date.getTime() <= end.getTime()) {
    const weekday = date.getDay() === 0 || date.getDay() === 6 ? 0.55 : 1;
    const season = 1 + 0.12 * Math.sin((2 * Math.PI * date.getTime()) / (365 * 86_400_000));
    const jitter = 0.85 + rand() * 0.3;
    // ~4% of days are OFF days (maintenance / no production): the API only
    // returns rows for days WITH data, so these are dropped from the
    // response entirely — the KPI's active-days count depends on it.
    const offDay = rand() < 0.04;
    if (!offDay) {
      const factor = weekday * season * jitter;
      values.push({
        date: new Date(date),
        jobCount: Math.round(DAILY_BASE.jobCount * factor),
        printedLength: Math.round(DAILY_BASE.printedLength * factor * 100) / 100,
        inkUsage: Math.round(DAILY_BASE.inkUsage * factor * 1000) / 1000,
        printTimeSeconds: Math.round(DAILY_BASE.printTimeSeconds * factor),
      });
    }
    date.setDate(date.getDate() + 1);
  }
  return values;
}

/**
 * One mock response "page" for the requested page granularity (`week`
 * arrives as days — API_GRANULARITY). Field names follow the dynamic
 * `Dbo520jh.K005.<granularity>` shape; measures are STRINGS like the API.
 */
export function generatePrintOverviewResponse(
  granularity: OverviewGranularity,
): PrintOverviewResponseRow[] {
  const api = API_GRANULARITY[granularity];
  const daily = generateDailyValues();

  const buckets = new Map<
    string,
    { iso: string; total: number; consumption: number; ink: number; time: number }
  >();
  for (const day of daily) {
    const y = day.date.getFullYear();
    const m = day.date.getMonth() + 1;
    const d = day.date.getDate();
    // API bucket key + the ISO timestamp its time field carries (first day
    // of the bucket).
    const key =
      api === "year"
        ? `${y}`
        : api === "month"
          ? `${y}-${pad(m)}`
          : `${y}-${pad(m)}-${pad(d)}`;
    const bucketIso =
      api === "year"
        ? isoOf(y, 1, 1)
        : api === "month"
          ? isoOf(y, m, 1)
          : isoOf(y, m, d);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.total += day.jobCount;
      bucket.consumption += day.printedLength;
      bucket.ink += day.inkUsage;
      bucket.time += day.printTimeSeconds;
    } else {
      buckets.set(key, {
        iso: bucketIso,
        total: day.jobCount,
        consumption: day.printedLength,
        ink: day.inkUsage,
        time: day.printTimeSeconds,
      });
    }
  }

  // Stringify the measures once, at the API boundary (rows are strings).
  return [...buckets.values()].map((bucket) => ({
    [`Dbo520jh.K005.${api}`]: bucket.iso,
    "Dbo520jh.K005": bucket.iso,
    "Dbo520jh.Total": String(bucket.total),
    "Dbo520jh.ConsumptionSum": String(bucket.consumption),
    "Dbo520jh.InkSum": String(bucket.ink),
    "Dbo520jh.TimeSum": String(bucket.time),
  }));
}
